import { Model } from '@nozbe/watermelondb'
import { children, date, field, json, text, writer } from '@nozbe/watermelondb/decorators'

import { HasManyAssociation } from '@nozbe/watermelondb/Model'
import {
  ChatDepartment,
  ChatDestinationVO,
  ChatSessionState,
  ComponentClientProfileVO,
  ComponentInfoChatLinksVO,
  ComponentInfoChatVO,
  ComponentInfoClientLinkVO,
  ComponentMessagesVO,
  ComponentTagVO,
  getChatSessionStateFrom
} from '@repo/types'
import { normalize } from '../predicates'
import Message from './Message'

class Chat extends Model {
  static table = 'chats'
  static associations: { messages: HasManyAssociation } = {
    messages: { type: 'has_many', foreignKey: 'chat_id' }
  }

  @text('connection_key') connectionKey: string
  @text('phone_number') phoneNumber: string
  @text('client_name') clientName: string
  @text('client_code') clientCode: string | null
  @text('client_summary') clientSummary: string | null
  @text('destination_description') destinationDescription: string | null
  @text('formatted_phone_number') formattedPhoneNumber: string
  @text('last_message') lastMessage: string
  @text('last_message_source') lastMessageSource: string
  @text('last_message_type') lastMessageType: string
  @text('last_message_user_name') lastMessageUserName: string
  @text('session_state') sessionState: ChatSessionState
  @text('user_id') userId: string
  @text('user_name') userName: string
  @text('vendor') vendor: string
  @text('normalized_search') normalizedSearch: string
  @text('key') key: string
  @text('department') department: ChatDepartment

  @date('client_summary_at') clientSummaryAt: Date | null
  @date('first_contact_at') firstContactAt: Date | null
  @date('contact_at') contactAt: Date

  @field('session_id') sessionId: number
  @field('unreads') unreads: number
  @field('minutes_to_block') minutesToBlock: number
  @field('is_highlighted') isHighlighted: boolean
  @field('is_show_message_user_name') isShowMessageUserName: boolean
  @field('is_archived') isArchived: boolean
  @field('is_blocked') isBlocked: boolean
  @field('is_contact_blocked') isContactBlocked: boolean
  @field('is_last_session') isLastSession: boolean
  @field('is_ai_session') isAiSession: boolean
  @field('is_owner') isOwner: boolean
  @field('has_client_message') hasClientMessage: boolean
  @field('is_client_summarizing') isClientSummarizing: boolean
  @field('show_client_summary') showClientSummary: boolean
  @field('is_chat_key') isChatKey: boolean
  @field('use_assistant') useAssistant: boolean

  @children('messages') messages: Message[]

  @json('destination', json => json) destination: ChatDestinationVO | null
  @json('tags', json => json) tags: ComponentTagVO[] | null

  setChatNormalizedSearch(chat: Chat, payload: ComponentInfoChatVO) {
    chat.normalizedSearch = `${normalize(payload.clientName)} ${normalize(payload.phoneNumber)} ${normalize(payload.userName)} ${this.setChatTagsNormalizedSearch(payload)} `
  }

  setChatTagsNormalizedSearch(payload: ComponentInfoChatVO) {
    return payload.tags?.map(it => normalize(it.title))
  }

  setChatByComponentInfoChatVO(vo: ComponentInfoChatVO) {
    this.sessionId = vo.sessionId
    this.connectionKey = vo.connectionKey
    this.phoneNumber = vo.phoneNumber
    this.clientName = vo.clientName
    this.clientCode = vo.clientCode ?? null
    this.clientSummary = vo.clientSummary ?? null
    this.clientSummaryAt = vo.clientSummaryAt ?? null
    this.isClientSummarizing = vo.clientSummarizing ?? false
    this.showClientSummary = vo.showClientSummary
    this.destinationDescription = vo.destinationDescription ?? null
    this.destination = vo.destination ?? null
    this.formattedPhoneNumber = vo.formattedPhoneNumber
    this.lastMessage = vo.lastMessage
    this.lastMessageSource = vo.lastMessageSource
    this.lastMessageUserName = vo.lastMessageUserName
    this.lastMessageType = vo.lastMessageType
    this.sessionState = getChatSessionStateFrom(vo.sessionState)
    this.userId = vo.userId
    this.userName = vo.userName
    this.vendor = vo.vendor
    this.firstContactAt = vo.firstContactAt ?? null
    this.contactAt = vo.contactAt
    this.isHighlighted = vo.highlighted
    this.isShowMessageUserName = vo.showMessageUserName
    this.isArchived = vo.archived
    this.unreads = vo.unreads
    this.key = vo.key
    this.isContactBlocked = vo.contactBlocked
    this.tags = vo.tags ?? null
    this.isAiSession = vo.aiSession
    this.isChatKey = `${vo.connectionKey}.${vo.phoneNumber}` === vo.key
    this.useAssistant = vo.useAssistant
    this.department = vo.department
    this.setChatNormalizedSearch(this, vo)
  }

  componentInfoVOByChat(): ComponentInfoChatVO {
    return {
      sessionId: this.sessionId,
      key: this.key,
      connectionKey: this.connectionKey,
      phoneNumber: this.phoneNumber,
      clientName: this.clientName,
      clientCode: this.clientCode ?? undefined,
      clientSummary: this.clientSummary ?? undefined,
      clientSummaryAt: this.clientSummaryAt ?? undefined,
      clientSummarizing: this.isClientSummarizing,
      showClientSummary: this.showClientSummary,
      destinationDescription: this.destinationDescription ?? undefined,
      destination: this.destination ?? undefined,
      formattedPhoneNumber: this.formattedPhoneNumber,
      lastMessage: this.lastMessage,
      lastMessageSource: this.lastMessageSource,
      lastMessageUserName: this.lastMessageUserName,
      lastMessageType: this.lastMessageType,
      sessionState: this.sessionState,
      userId: this.userId,
      userName: this.userName,
      vendor: this.vendor.toUpperCase(),
      firstContactAt: this.firstContactAt ?? undefined,
      contactAt: this.contactAt,
      highlighted: this.isHighlighted,
      showMessageUserName: this.isShowMessageUserName,
      archived: this.isArchived,
      unreads: this.unreads,
      contactBlocked: this.isContactBlocked,
      blocked: this.isBlocked,
      tags: this.tags ?? undefined,
      lastSession: this.isLastSession,
      aiSession: this.isAiSession,
      owner: this.isOwner,
      useAssistant: this.useAssistant,
      department: this.department
    }
  }

  componentInfoChatLinksVOByChat(): ComponentInfoChatLinksVO {
    return {
      clientCode: this.clientCode ?? undefined,
      clientName: this.clientName,
      connectionKey: this.connectionKey,
      destination: this.destination ?? undefined
    }
  }

  componentInfoClientLinkVOByChat(): ComponentInfoClientLinkVO {
    return {
      clientCode: this.clientCode ?? undefined,
      destination: this.destination ?? undefined
    }
  }

  componentClientProfileVOByChat(department: string): ComponentClientProfileVO {
    return {
      connectionKey: this.connectionKey ?? undefined,
      phoneNumber: this.phoneNumber ?? undefined,
      destination: this.destination ?? undefined,
      department: department ?? undefined
    }
  }

  showTakeSessionMessage(): boolean {
    return !!(
      !this.isBlocked &&
      !this.isContactBlocked &&
      ((this.isArchived && (!this.isLastSession || !this.isOwner)) ||
        this.destination?.server === 'WINGO' ||
        this.destination?.server?.includes('TECFLOW'))
    )
  }

  markAsArchived(payload: ComponentInfoChatVO) {
    this.isArchived = true
    this.isLastSession = payload.lastSession ?? this.isLastSession
    this.isOwner = payload.owner ?? this.isOwner
  }

  @writer
  async updateBlockedInfo(
    payload?: Omit<ComponentMessagesVO, 'messages' | 'archived'> & Partial<Pick<ComponentMessagesVO, 'archived'>>
  ) {
    await this.update(chat => {
      chat.minutesToBlock = payload?.minutesToBlock ?? chat.minutesToBlock
      chat.isBlocked = payload?.blocked ?? chat.isBlocked
      chat.isArchived = payload?.archived ?? chat.isArchived
      chat.hasClientMessage = payload?.hasClientMessage ?? chat.hasClientMessage
      chat.isOwner = payload?.owner ?? chat.isOwner
      chat.isLastSession = payload?.lastSession ?? chat.isLastSession
    })
  }

  @writer
  async updateSummarizing() {
    await this.update(chat => {
      chat.isClientSummarizing = true
    })
  }
}

export default Chat
